import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useData } from "../contexts/DataContext";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Loader2, DollarSign, User, MapPin, Settings2, Wrench, ArrowRight, FileText, Trash2, ChevronRight, ChevronLeft, ChevronDown, SlidersHorizontal, Clock, Check, X, Settings, Info, Search } from "lucide-react";
import { KanbanSkeleton } from "../components/skeletons";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Deterministic pastel color per name letter — Proline-style avatar. Same
// name always gets the same color across sessions.
const AVATAR_PALETTE = [
  "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899", "#10B981",
  "#EF4444", "#0EA5E9", "#F97316", "#6366F1", "#84CC16",
  "#14B8A6", "#A855F7", "#F43F5E", "#22C55E", "#06B6D4",
];
const letterAvatarColor = (name) => {
  const s = String(name || "?").trim().toUpperCase();
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

/**
 * MobilePipelineStage — Proline-inspired mobile pipeline view.
 *
 * - Compact stage picker chip pinned to the top-left corner
 * - Horizontal swipe on the board to move between stages
 * - Cards: bold name → thin divider → address → "Quote sent Xd" chip
 *   that counts up while the quote is still open and freezes at the
 *   number of days it took to close the moment it gets signed.
 * - Long-press a card → bottom sheet lists every stage + primary actions
 *   (advance / new quote / open / delete). Effectively "drag to any
 *   stage" without needing a multi-column view on a small screen.
 * - Per-stage filter (lead source + classification) via a small icon
 *   next to the stage chip.
 */
function MobilePipelineStage({ stages, projects, contacts, quotes, projectCardValue, projectCardValueIsEstimate, navigate, searchActive }) {
  const [activeStageIdx, setActiveStageIdx] = useState(0);
  const [showStagePicker, setShowStagePicker] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filter, setFilter] = useState({ lead_source: "", classification: "" });
  const [quickActionProject, setQuickActionProject] = useState(null);
  const [movingTo, setMovingTo] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const pressTimer = useRef(null);
  const touchStartRef = useRef(null);

  // Reset active stage if the category swaps out from under us.
  useEffect(() => {
    if (activeStageIdx >= stages.length) setActiveStageIdx(0);
  }, [stages, activeStageIdx]);

  // While searching, land on a stage that actually holds a match — mobile
  // shows one column at a time, so staying on stage 1 looked like "no
  // results" even when the hit was two columns over. Only applies during
  // search so normal browsing never yanks the column out from under you.
  const countsPerStage = stages.map(s => projects.filter(p => p.stage === s.value).length);
  const countsKey = countsPerStage.join(",");
  useEffect(() => {
    if (!searchActive) return;
    const counts = countsKey ? countsKey.split(",").map(Number) : [];
    if ((counts[activeStageIdx] || 0) > 0) return;
    const firstHit = counts.findIndex(n => n > 0);
    if (firstHit >= 0) setActiveStageIdx(firstHit);
  }, [searchActive, countsKey, activeStageIdx]);

  const stage = stages[activeStageIdx];
  if (!stage) return null;

  // Filter and sort projects for the visible stage. Most-recent-activity
  // first (updated_at DESC) so the newest quote/card sits at the top.
  const stageProjects = projects.filter(p => {
    if (p.stage !== stage.value) return false;
    if (filter.lead_source && p.lead_source !== filter.lead_source) return false;
    if (filter.classification && p.classification !== filter.classification) return false;
    return true;
  }).sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
  const activeFilterCount = (filter.lead_source ? 1 : 0) + (filter.classification ? 1 : 0);
  const uniqueLeadSources = Array.from(new Set(projects.map(p => p.lead_source).filter(Boolean)));
  const uniqueClassifications = Array.from(new Set(projects.map(p => p.classification).filter(Boolean)));

  // "Quote sent Xd" chip logic.
  // Prefer an OPEN (sent but not yet signed) quote so the counter keeps
  // climbing; cap at 365 days so a stale/abandoned quote doesn't run to
  // 4-digit numbers. Fall back to the most recently signed quote and
  // freeze the count at (approved_at − sent_at) so the card shows how
  // long it took to close, forever.
  const projectQuoteAge = (projectId) => {
    const projectQuotes = (quotes || []).filter(q => q.project_id === projectId);
    if (!projectQuotes.length) return null;
    const isSigned = (q) => q.approved_at || q.status === "approved" || q.status === "signed";
    const open = projectQuotes.find(q => q.sent_at && !isSigned(q));
    if (open) {
      const ms = Date.now() - new Date(open.sent_at).getTime();
      const days = Math.max(0, Math.floor(ms / 86_400_000));
      return { days: Math.min(days, 365), frozen: false, capped: days >= 365 };
    }
    const signed = projectQuotes
      .filter(q => q.approved_at && q.sent_at)
      .sort((a, b) => new Date(b.approved_at) - new Date(a.approved_at))[0];
    if (signed) {
      const ms = new Date(signed.approved_at).getTime() - new Date(signed.sent_at).getTime();
      const days = Math.max(0, Math.floor(ms / 86_400_000));
      return { days, frozen: true, capped: false };
    }
    return null;
  };

  // Swipe left = next stage, swipe right = previous stage. 55px threshold
  // so vertical scroll wins for any near-vertical drag.
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchStartRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    touchStartRef.current = null;
    if (Math.abs(dx) < 55 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0 && activeStageIdx < stages.length - 1) setActiveStageIdx(activeStageIdx + 1);
    else if (dx > 0 && activeStageIdx > 0) setActiveStageIdx(activeStageIdx - 1);
  };

  const moveToStage = async (project, stageValue) => {
    if (!project || project.stage === stageValue) { setQuickActionProject(null); return; }
    setMovingTo(stageValue);
    try {
      await axios.put(`${API}/projects/${project.id}`, { stage: stageValue });
      const stageName = stages.find(s => s.value === stageValue)?.name || stageValue;
      toast.success(`Moved to ${stageName}`);
      setQuickActionProject(null);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to move");
    } finally {
      setMovingTo(null);
    }
  };

  const deleteProject = async (project) => {
    if (!window.confirm(`Delete the ${(contacts.find(c => c.id === project.contact_id)?.name) || "this"} project? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/projects/${project.id}`);
      toast.success("Project deleted");
      setQuickActionProject(null);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const startPress = (project) => {
    pressTimer.current = setTimeout(() => {
      try { navigator.vibrate?.(10); } catch {}
      setQuickActionProject(project);
    }, 450);
  };
  const cancelPress = () => {
    if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
  };

  const canGoPrev = activeStageIdx > 0;
  const canGoNext = activeStageIdx < stages.length - 1;

  return (
    <div className="sm:hidden" data-testid="pipeline-mobile-view">
      {/* Stage pill on its own row (left-anchored, Proline style) with
          settings gear + filter icon on the right */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <button
          type="button"
          onClick={() => setShowStagePicker(true)}
          className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[13px] font-bold uppercase tracking-wide shadow-sm border transition-all active:scale-[0.98]"
          style={{
            backgroundColor: `${stage.color}18`,
            borderColor: `${stage.color}55`,
            color: stage.color,
          }}
          data-testid="pipeline-stage-chip"
        >
          <span className="truncate max-w-[55vw]">{stage.name}</span>
          <span className="opacity-70">—</span>
          <span className="tabular-nums">{stageProjects.length}</span>
          <ChevronDown className="w-4 h-4 opacity-70" />
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`relative inline-flex items-center justify-center h-9 w-9 rounded-full border transition-all active:scale-[0.98] ${activeFilterCount ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-slate-500 border-slate-200"}`}
            data-testid="pipeline-filter-chip"
            aria-label="Filter this stage"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{activeFilterCount}</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate("/settings", { state: { defaultTab: "pipeline" } })}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-slate-200 bg-white text-slate-500 transition-all active:scale-[0.98]"
            data-testid="pipeline-settings-gear"
            aria-label="Pipeline settings"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Compact swipe hint */}
      <div className="flex items-center justify-between mb-3 text-[10px] text-slate-400 px-1">
        <button
          type="button"
          disabled={!canGoPrev}
          onClick={() => canGoPrev && setActiveStageIdx(activeStageIdx - 1)}
          className="inline-flex items-center gap-0.5 disabled:opacity-30"
          data-testid="pipeline-prev-stage"
        >
          <ChevronLeft className="w-3 h-3" />
          {canGoPrev ? stages[activeStageIdx - 1].name : ""}
        </button>
        <span>swipe to switch · {activeStageIdx + 1}/{stages.length}</span>
        <button
          type="button"
          disabled={!canGoNext}
          onClick={() => canGoNext && setActiveStageIdx(activeStageIdx + 1)}
          className="inline-flex items-center gap-0.5 disabled:opacity-30"
          data-testid="pipeline-next-stage"
        >
          {canGoNext ? stages[activeStageIdx + 1].name : ""}
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Swipeable card list — wrapped in a closed-off container so each
          stage feels like a distinct board, Proline-style. */}
      <div
        className="rounded-2xl border border-slate-200 bg-slate-50/60 shadow-sm p-2 space-y-2 min-h-[60vh]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="pipeline-swipe-area"
      >
        {stageProjects.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm rounded-xl border border-dashed border-slate-200 bg-white">
            No projects in <span className="font-medium">{stage.name}</span>
            {activeFilterCount > 0 && <p className="mt-2 text-xs">Filter is active — clear it to see more</p>}
          </div>
        ) : (
          stageProjects.map(project => {
            const contact = contacts.find(c => c.id === project.contact_id);
            const age = projectQuoteAge(project.id);
            const cardValue = projectCardValue ? projectCardValue(project.id) : 0;
            const isEstimate = projectCardValueIsEstimate ? projectCardValueIsEstimate(project.id) : false;
            return (
              <button
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`, { state: { from: "/pipeline" } })}
                onTouchStart={() => startPress(project)}
                onTouchEnd={cancelPress}
                onTouchCancel={cancelPress}
                onTouchMove={cancelPress}
                onContextMenu={(e) => { e.preventDefault(); setQuickActionProject(project); }}
                className="w-full text-left bg-white rounded-xl border border-slate-200 p-3.5 active:bg-slate-50 transition-colors select-none shadow-sm"
                data-testid={`pipeline-mobile-card-${project.id}`}
              >
                {/* Top row — letter avatar + name + day counter */}
                <div className="flex items-center gap-3">
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
                    style={{ backgroundColor: letterAvatarColor(contact?.name) }}
                  >
                    {(contact?.name || "?").trim().charAt(0).toUpperCase()}
                  </div>
                  <p className="flex-1 min-w-0 text-lg font-bold text-slate-900 truncate" data-testid={`pipeline-card-name-${project.id}`}>
                    {contact?.name || "Unknown"}
                  </p>
                  {age !== null && (
                    <span
                      className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums flex-shrink-0 ${
                        age.frozen
                          ? "text-emerald-600"
                          : age.capped
                            ? "text-rose-700"
                            : age.days >= 30
                              ? "text-rose-600"
                              : age.days >= 14
                                ? "text-amber-600"
                                : age.days >= 7
                                  ? "text-amber-500"
                                  : "text-slate-500"
                      }`}
                      data-testid={`pipeline-card-age-${project.id}`}
                      title={age.frozen ? `Signed after ${age.days} day${age.days === 1 ? "" : "s"}` : age.capped ? "Open for 365+ days — capped" : `Quote sent ${age.days} day${age.days === 1 ? "" : "s"} ago`}
                    >
                      {age.days}{age.capped ? "+" : ""}
                      <Clock className="w-4 h-4" />
                    </span>
                  )}
                </div>

                {/* Address row with dotted leader — Proline style */}
                <div className="mt-2.5 flex items-baseline gap-2 text-xs">
                  <span className="text-slate-400 font-medium flex-shrink-0">Address</span>
                  <span className="flex-1 border-b border-dotted border-slate-300 translate-y-[-3px]" />
                  <span className="text-slate-700 font-medium truncate max-w-[60%]" title={project.project_address || ""}>
                    {project.project_address || <span className="italic text-slate-300">—</span>}
                  </span>
                </div>

                {/* Referral + estimate row — surfaces the SkyQuote estimated
                    price and any referrer name without opening the card. */}
                {(project.referral_name || cardValue > 0) && (
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                    {project.referral_name ? (
                      <span className="text-slate-500 truncate">
                        Referred by <span className="font-medium text-slate-700">{project.referral_name}</span>
                      </span>
                    ) : <span />}
                    {cardValue > 0 && (
                      isEstimate ? (
                        <span
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 tabular-nums flex-shrink-0"
                          title="Estimate — customer has not signed a quote yet"
                          data-testid={`pipeline-mobile-estimate-${project.id}`}
                        >
                          Est <DollarSign className="w-3 h-3" />{cardValue.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-emerald-600 flex items-center tabular-nums flex-shrink-0">
                          <DollarSign className="w-3 h-3" />{cardValue.toLocaleString()}
                        </span>
                      )
                    )}
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Stage picker bottom sheet — full list of stages, big tap targets */}
      <Sheet open={showStagePicker} onOpenChange={setShowStagePicker}>
        <SheetContent side="bottom" className="rounded-t-2xl px-4 pt-4 pb-8 max-h-[80vh] overflow-y-auto" data-testid="pipeline-stage-picker-sheet">
          <SheetHeader className="text-left mb-3">
            <SheetTitle className="text-lg">Jump to stage</SheetTitle>
            <SheetDescription className="text-sm">Or swipe left/right on the board</SheetDescription>
          </SheetHeader>
          <div className="space-y-1.5">
            {stages.map((s, i) => {
              const n = projects.filter(p => p.stage === s.value).length;
              const isActive = i === activeStageIdx;
              return (
                <button
                  key={s.value}
                  onClick={() => { setActiveStageIdx(i); setShowStagePicker(false); }}
                  className={`w-full flex items-center justify-between gap-3 h-14 px-4 rounded-xl text-base font-medium transition-colors ${
                    isActive ? "text-white" : "bg-white border border-slate-200 text-slate-800 active:bg-slate-50"
                  }`}
                  style={isActive ? { backgroundColor: s.color } : {}}
                  data-testid={`pipeline-stage-picker-${s.value}`}
                >
                  <span className="flex items-center gap-3">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: isActive ? "white" : s.color }} />
                    {s.name}
                  </span>
                  <span className={`text-sm font-bold tabular-nums ${isActive ? "text-white/90" : "text-slate-400"}`}>{n}</span>
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Filter bottom sheet */}
      <Sheet open={showFilter} onOpenChange={setShowFilter}>
        <SheetContent side="bottom" className="rounded-t-2xl px-4 pt-4 pb-8 max-h-[80vh] overflow-y-auto" data-testid="pipeline-filter-sheet">
          <SheetHeader className="text-left mb-3">
            <SheetTitle className="text-lg">Filter this stage</SheetTitle>
            <SheetDescription className="text-sm">Narrows only projects currently in <span className="font-medium">{stage.name}</span></SheetDescription>
          </SheetHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase font-bold text-slate-400 tracking-wider mb-2">Lead source</p>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setFilter(f => ({ ...f, lead_source: "" }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${!filter.lead_source ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-slate-600 border-slate-200"}`}
                >Any</button>
                {uniqueLeadSources.map(src => (
                  <button
                    key={src}
                    onClick={() => setFilter(f => ({ ...f, lead_source: src }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize ${filter.lead_source === src ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-slate-600 border-slate-200"}`}
                  >{src.replace(/_/g, " ")}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase font-bold text-slate-400 tracking-wider mb-2">Classification</p>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setFilter(f => ({ ...f, classification: "" }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${!filter.classification ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-slate-600 border-slate-200"}`}
                >Any</button>
                {uniqueClassifications.map(c => (
                  <button
                    key={c}
                    onClick={() => setFilter(f => ({ ...f, classification: c }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize ${filter.classification === c ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-slate-600 border-slate-200"}`}
                  >{c.replace(/_/g, " ")}</button>
                ))}
              </div>
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={() => setFilter({ lead_source: "", classification: "" })}
                className="w-full h-11 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 active:bg-slate-50"
              >
                Clear filters
              </button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Long-press quick actions sheet — includes "Move to any stage" */}
      <Sheet open={!!quickActionProject} onOpenChange={(o) => !o && setQuickActionProject(null)}>
        <SheetContent side="bottom" className="rounded-t-2xl px-4 pt-4 pb-8 max-h-[85vh] overflow-y-auto" data-testid="pipeline-quick-actions-sheet">
          <SheetHeader className="text-left mb-3">
            <SheetTitle className="text-lg">
              {(contacts.find(c => c.id === quickActionProject?.contact_id)?.name) || "Project"}
            </SheetTitle>
            <SheetDescription className="text-sm">
              Currently in <span className="font-medium">{stage.name}</span>
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-3">
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-2 px-1">Move to stage</p>
              <div className="space-y-1.5">
                {stages.filter(s => s.value !== quickActionProject?.stage).map(s => (
                  <button
                    key={s.value}
                    onClick={() => quickActionProject && moveToStage(quickActionProject, s.value)}
                    disabled={!!movingTo}
                    className="w-full flex items-center justify-between gap-3 h-12 px-4 rounded-xl bg-white border border-slate-200 text-slate-800 text-sm font-medium active:bg-slate-50 disabled:opacity-50"
                    data-testid={`pipeline-qa-move-${s.value}`}
                  >
                    <span className="flex items-center gap-2.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                      {s.name}
                    </span>
                    {movingTo === s.value ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-300" />}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-2 px-1">Actions</p>
              <div className="space-y-1.5">
                <button
                  onClick={() => { const p = quickActionProject; setQuickActionProject(null); navigate(`/quotes/new?project_id=${p.id}`); }}
                  className="w-full flex items-center justify-between gap-3 h-12 px-4 rounded-xl bg-white border border-slate-200 text-slate-800 text-sm font-medium active:bg-slate-50"
                  data-testid="pipeline-qa-quote"
                >
                  <span className="flex items-center gap-2.5"><FileText className="w-4 h-4 text-[#00A3E0]" /> New quote</span>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </button>
                <button
                  onClick={() => { const p = quickActionProject; setQuickActionProject(null); navigate(`/projects/${p.id}`, { state: { from: "/pipeline" } }); }}
                  className="w-full flex items-center justify-between gap-3 h-12 px-4 rounded-xl bg-white border border-slate-200 text-slate-800 text-sm font-medium active:bg-slate-50"
                  data-testid="pipeline-qa-open"
                >
                  <span className="flex items-center gap-2.5"><User className="w-4 h-4 text-slate-500" /> Open project</span>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </button>
                <button
                  onClick={() => quickActionProject && deleteProject(quickActionProject)}
                  disabled={deleting}
                  className="w-full flex items-center gap-2.5 h-12 px-4 rounded-xl bg-white border border-red-200 text-red-600 text-sm font-medium active:bg-red-50 disabled:opacity-50"
                  data-testid="pipeline-qa-delete"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function Pipeline() {
  const navigate = useNavigate();
  const {
    projects, fetchProjects, updateProjectStage,
    contacts, fetchContacts,
    quotes, fetchQuotes,
    pipelineStages, fetchPipelineStages,
    pipelineCategories, fetchPipelineCategories, loading
  } = useData();

  const [activeCategory, setActiveCategory] = useState(null);
  const [healing, setHealing] = useState(false);
  // Pipeline-wide search — filters cards by contact name or project
  // address across every stage. Ref is auto-focused on mount so users
  // landing here right after login can start typing immediately.
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      try { searchInputRef.current?.focus({ preventScroll: true }); } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, []);

  const searchLower = searchQuery.trim().toLowerCase();
  const contactById = new Map((contacts || []).map(c => [c.id, c]));
  const onlyDigits = (s) => String(s || "").replace(/\D/g, "");
  const searchDigits = onlyDigits(searchQuery);
  // Quote numbers per project, so searching "PENLEY-002" finds the job.
  const quoteNumbersByProject = new Map();
  (quotes || []).forEach(q => {
    if (!q.project_id) return;
    const arr = quoteNumbersByProject.get(q.project_id) || [];
    arr.push(String(q.quote_number || "").toLowerCase());
    quoteNumbersByProject.set(q.project_id, arr);
  });
  const projectMatchesSearch = (p) => {
    if (!searchLower) return true;
    const c = contactById.get(p.contact_id) || {};
    const haystack = [c.name, c.email, p.project_address, p.name]
      .map(x => String(x || "").toLowerCase());
    if (haystack.some(h => h && h.includes(searchLower))) return true;
    if ((quoteNumbersByProject.get(p.id) || []).some(n => n && n.includes(searchLower))) return true;
    // Phone: compare digits only so "5551234" matches "(555) 123-4..."
    if (searchDigits.length >= 4) {
      const phone = onlyDigits(c.phone);
      if (phone && phone.includes(searchDigits)) return true;
    }
    return false;
  };
  // Collapsed columns per category — persisted to localStorage so a rep's
  // preferred view (e.g. Roofing: keep Quote Sent + Approved open, everything
  // else collapsed) survives navigation and refresh. Keyed as `{category}:{stage_value}`.
  const [collapsedStages, setCollapsedStages] = useState(() => {
    try {
      const raw = localStorage.getItem("pipeline_collapsed_stages_v1");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const toggleStageCollapsed = (stageValue) => {
    setCollapsedStages(prev => {
      const key = `${activeCategory}:${stageValue}`;
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem("pipeline_collapsed_stages_v1", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const isStageCollapsed = (stageValue) => !!collapsedStages[`${activeCategory}:${stageValue}`];

  const handleHealOrphans = async () => {
    if (!window.confirm("Find projects sitting on a stage that no longer exists and move them back onto a real stage? It restores the last stage you picked where that's known, otherwise the Inquiry Received stage of that job's own service. It will never mark a job Paid & Closed.")) return;
    setHealing(true);
    try {
      const res = await axios.post(`${API}/pipeline-stages/heal-orphans`);
      const fixes = res.data?.fixes || [];
      if (fixes.length === 0) {
        toast.success("No orphans found — every project is on a valid stage");
      } else {
        const names = fixes.slice(0, 3).map(f => f.contact || f.project_id.slice(0, 6)).join(", ");
        toast.success(
          `Recovered ${fixes.length} project${fixes.length === 1 ? "" : "s"}: ${names}${fixes.length > 3 ? "…" : ""}`,
          { duration: 8000 },
        );
        await fetchProjects();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Heal failed");
    } finally {
      setHealing(false);
    }
  };

  useEffect(() => {
    fetchProjects(); fetchContacts(); fetchQuotes(); fetchPipelineStages(); fetchPipelineCategories();
  }, [fetchProjects, fetchContacts, fetchQuotes, fetchPipelineStages, fetchPipelineCategories]);

  // Set default active category once loaded
  useEffect(() => {
    if (pipelineCategories.length > 0 && !activeCategory) {
      setActiveCategory(pipelineCategories[0].value);
    }
  }, [pipelineCategories, activeCategory]);

  const categoryStages = pipelineStages.filter(s => s.category === activeCategory);
  const activeCat = pipelineCategories.find(c => c.value === activeCategory);

  // Preferred card value: sum of approved/signed quote totals on this project
  // (the true contract). Falls back to the manually-entered `estimated_value`
  // when the project has no signed quote yet. Prevents pipeline cards from
  // showing a stale estimate after the customer signs at a different amount.
  const projectCardValue = (projectId) => {
    const signedQuotes = (quotes || []).filter(
      q => q.project_id === projectId && (q.status === "approved" || q.status === "signed")
    );
    if (signedQuotes.length > 0) {
      const sum = signedQuotes.reduce(
        (s, q) => s + Number(q.approved_total ?? q.total ?? 0),
        0,
      );
      if (sum > 0) return sum;
    }
    const p = projects.find(pr => pr.id === projectId);
    return Number(p?.estimated_value || 0);
  };

  // True when the card's dollar value came from the SkyQuote/manual
  // `estimated_value` (i.e. no signed quote yet). Lets us prefix "Est"
  // on the badge so reps can tell at a glance whether a card is still
  // a lead vs a signed contract without opening it.
  const projectCardValueIsEstimate = (projectId) => {
    const signedQuotes = (quotes || []).filter(
      q => q.project_id === projectId && (q.status === "approved" || q.status === "signed")
    );
    if (signedQuotes.length > 0) {
      const sum = signedQuotes.reduce(
        (s, q) => s + Number(q.approved_total ?? q.total ?? 0),
        0,
      );
      if (sum > 0) return false;
    }
    const p = projects.find(pr => pr.id === projectId);
    return Number(p?.estimated_value || 0) > 0;
  };

  // Get all stage values for the active category to filter projects
  const categoryStageValues = categoryStages.map(s => s.value);
  const categoryProjects = projects.filter(p => categoryStageValues.includes(p.stage));

  // ---------------------------------------------------------------------
  // Cross-pipeline search. Searching used to filter only the tab you were
  // standing on, with tab counts frozen at their unfiltered totals — so a
  // job living in another pipeline looked like "no results". Now every
  // pipeline is searched at once, each tab shows its own match count, and
  // we jump you to a tab that actually has hits.
  // ---------------------------------------------------------------------
  const stageByValue = new Map(pipelineStages.map(s => [s.value, s]));
  const categoryByValue = new Map(pipelineCategories.map(c => [c.value, c]));
  const matchingProjects = searchLower ? projects.filter(projectMatchesSearch) : [];
  const matchCountByCategory = {};
  matchingProjects.forEach(p => {
    const cat = stageByValue.get(p.stage)?.category || "__unassigned__";
    matchCountByCategory[cat] = (matchCountByCategory[cat] || 0) + 1;
  });
  // Matches whose stage isn't in ANY pipeline — otherwise invisible everywhere.
  const unassignedMatches = matchingProjects.filter(p => !stageByValue.has(p.stage));
  const matchCountKey = JSON.stringify(matchCountByCategory);

  useEffect(() => {
    if (!searchLower) return;
    const counts = JSON.parse(matchCountKey);
    if ((counts[activeCategory] || 0) > 0) return;
    const firstHit = pipelineCategories.find(c => (counts[c.value] || 0) > 0);
    if (firstHit) setActiveCategory(firstHit.value);
  }, [searchLower, matchCountKey, activeCategory, pipelineCategories]);

  const onDragEnd = async (result) => {
    const { draggableId, destination } = result;
    if (!destination) return;
    const targetStage = destination.droppableId;
    const project = projects.find(p => p.id === draggableId);
    if (!project || project.stage === targetStage) return;
    try {
      await updateProjectStage(draggableId, targetStage);
      const stageName = pipelineStages.find(s => s.value === targetStage)?.name || targetStage;
      toast.success(`Moved to ${stageName}`);
    } catch {
      toast.error("Failed to update stage");
    }
  };

  // Count projects per category. While searching, show how many MATCHES
  // each tab holds instead of its unfiltered total — otherwise the counts
  // contradict what's on screen.
  const getCategoryCount = (catValue) => {
    if (searchLower) return matchCountByCategory[catValue] || 0;
    const catStageValues = pipelineStages.filter(s => s.category === catValue).map(s => s.value);
    return projects.filter(p => catStageValues.includes(p.stage)).length;
  };

  return (
    <div className="min-h-screen">
      <Header title="Pipeline">
        <Button variant="outline" className="h-10" onClick={handleHealOrphans} disabled={healing} data-testid="pipeline-heal-btn" title="Find projects that have disappeared from every tab and snap them back to a real stage">
          {healing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
          Recover Missing
        </Button>
        <Button variant="outline" className="h-10" onClick={() => navigate("/settings")} data-testid="pipeline-manage-btn">
          <Settings2 className="w-4 h-4 mr-2" />Manage Stages
        </Button>
      </Header>

      {/* Pipeline-wide search — auto-focused on mount so post-login taps
          straight into it. Filters cards by contact name, project name,
          and project address across every stage/category simultaneously. */}
      <div className="bg-white border-b border-slate-100 px-4 sm:px-6 py-3">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="search"
            placeholder="Name, address, phone, email or quote #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 pl-9 pr-9 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:bg-white focus:border-[#0F4C81] focus:ring-2 focus:ring-[#0F4C81]/20 outline-none transition-all"
            data-testid="pipeline-search-input"
            autoComplete="off"
            spellCheck={false}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 rounded"
              title="Clear search"
              data-testid="pipeline-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Cross-pipeline results. Searching covers EVERY pipeline, so this
            tells you where each hit actually lives and lets you jump
            straight to it — including jobs whose stage is broken and which
            therefore appear on no board at all. */}
        {searchLower && (
          <div className="mt-3" data-testid="pipeline-search-results">
            {matchingProjects.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="pipeline-search-empty">
                No projects match "{searchQuery}" in any pipeline.
              </p>
            ) : (
              <>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  {matchingProjects.length} match{matchingProjects.length === 1 ? "" : "es"} across all pipelines
                </p>
                <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto pr-1">
                  {matchingProjects.slice(0, 40).map(p => {
                    const c = contactById.get(p.contact_id) || {};
                    const st = stageByValue.get(p.stage);
                    const cat = st ? categoryByValue.get(st.category) : null;
                    return (
                      <div
                        key={p.id}
                        className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 hover:border-[#0F4C81]/40 transition-colors"
                        data-testid={`pipeline-search-result-${p.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => navigate(`/projects/${p.id}`)}
                          className="text-sm font-semibold text-slate-900 hover:text-[#0F4C81] text-left"
                          data-testid={`pipeline-search-open-${p.id}`}
                        >
                          {c.name || p.name || "Unnamed"}
                        </button>
                        {p.project_address && (
                          <span className="text-xs text-slate-500 truncate max-w-[220px]">{p.project_address}</span>
                        )}
                        {st ? (
                          <button
                            type="button"
                            onClick={() => setActiveCategory(st.category)}
                            className="text-[11px] font-bold px-2 py-0.5 rounded-full hover:opacity-80 transition-opacity"
                            style={{ backgroundColor: `${st.color}22`, color: st.color }}
                            title={`Jump to the ${cat?.name || st.category} board`}
                            data-testid={`pipeline-search-jump-${p.id}`}
                          >
                            {(cat?.name || st.category)} › {st.name}
                          </button>
                        ) : (
                          <span
                            className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                            title={`Stage "${p.stage}" isn't in any pipeline, so this card shows on no board. Use Recover Missing.`}
                            data-testid={`pipeline-search-invalid-${p.id}`}
                          >
                            Broken stage: {p.stage || "(none)"}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {unassignedMatches.length > 0 && (
                  <p className="mt-2 text-xs text-amber-700" data-testid="pipeline-search-invalid-note">
                    {unassignedMatches.length} match{unassignedMatches.length === 1 ? " sits" : "es sit"} on a stage that isn't in any pipeline and won't show on a board — click "Recover Missing" to repair.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Category Tabs */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6">
        <div className="flex items-center gap-1 overflow-x-auto py-2 -mb-px">
          {pipelineCategories.map(cat => (
            <button
              key={cat.value}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-all border-b-2 ${
                activeCategory === cat.value
                  ? "border-[#0F4C81] text-[#0F4C81] bg-[#0F4C81]/5"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
              onClick={() => setActiveCategory(cat.value)}
              data-testid={`pipeline-tab-${cat.value}`}
            >
              <span className="w-2.5 h-2.5 rounded-full inline-block mr-2" style={{ backgroundColor: cat.color }} />
              {cat.name}
              <Badge variant="secondary" className="ml-2 text-xs">{getCategoryCount(cat.value)}</Badge>
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {loading ? (
          <KanbanSkeleton columns={4} cardsPerCol={3} />
        ) : categoryStages.length > 0 ? (
          <DragDropContext onDragEnd={onDragEnd}>
            {/* Desktop kanban — horizontal scroll retained here intentionally */}
            <div className="hidden sm:flex gap-4 overflow-x-auto pb-6">
              {categoryStages.map(stage => {
                // Sort every column by most-recent-activity DESC so the newest
                // card sits on top. For Quote Sent this means the freshest
                // quote is what you see first when you open the column. For
                // every other stage it means "the one you or the system
                // touched most recently is up top" — matches how you'd
                // naturally scan a to-do list. Search query filters across
                // contact name, project name, and project address.
                const stageProjects = projects
                  .filter(p => p.stage === stage.value)
                  .filter(projectMatchesSearch)
                  .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
                const totalValue = stageProjects.reduce((sum, p) => sum + projectCardValue(p.id), 0);
                const collapsed = isStageCollapsed(stage.value);

                // Collapsed sliver — 44px wide, rotated header, tap to expand.
                // `self-start` prevents the flex parent from stretching the
                // sliver to match the tallest expanded column (would push the
                // rotated label off-screen).
                if (collapsed) {
                  return (
                    <div
                      key={stage.id}
                      className="flex-shrink-0 self-start w-11 h-[420px] bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden hover:border-slate-300 transition-colors"
                      data-testid={`pipeline-column-collapsed-${stage.value}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleStageCollapsed(stage.value)}
                        className="h-full w-full flex flex-col items-center py-3 gap-4 group"
                        title={`Expand ${stage.name} (${stageProjects.length})`}
                        data-testid={`pipeline-column-expand-${stage.value}`}
                      >
                        <span
                          className="inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-full text-[11px] font-bold tabular-nums flex-shrink-0"
                          style={{
                            backgroundColor: `${stage.color}22`,
                            color: stage.color,
                          }}
                        >
                          {stageProjects.length}
                        </span>
                        <span
                          className="text-[11px] font-bold uppercase tracking-wider whitespace-nowrap flex-1"
                          style={{
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                            color: stage.color,
                          }}
                        >
                          {stage.name}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
                      </button>
                    </div>
                  );
                }

                return (
                  <div
                    key={stage.id}
                    className="flex-shrink-0 w-72 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden"
                    data-testid={`pipeline-column-${stage.value}`}
                  >
                    {/* Stage header bar — closed-off Proline-style container.
                        Pill uses the stage's own color at low opacity so the
                        column is instantly recognisable in a busy category. */}
                    <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-slate-100 bg-slate-50/70">
                      <button
                        type="button"
                        onClick={() => toggleStageCollapsed(stage.value)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase truncate hover:opacity-80 transition-opacity"
                        style={{
                          backgroundColor: `${stage.color}22`,
                          color: stage.color,
                        }}
                        title={`Collapse ${stage.name}`}
                        data-testid={`pipeline-column-collapse-${stage.value}`}
                      >
                        <ChevronLeft className="w-3 h-3 opacity-60 flex-shrink-0" />
                        <span className="truncate">{stage.name}</span>
                        <span className="opacity-70">— {stageProjects.length}</span>
                      </button>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => navigate("/settings")}
                          className="p-1 rounded hover:bg-slate-200/60 text-slate-400 hover:text-slate-700 transition-colors"
                          title="Configure this stage"
                          data-testid={`pipeline-column-settings-${stage.value}`}
                        >
                          <Settings className="w-3.5 h-3.5" />
                        </button>
                        <span
                          className="p-1 text-slate-400"
                          title={totalValue > 0 ? `Total value: $${totalValue.toLocaleString()}` : "No estimate values in this stage"}
                        >
                          <Info className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>

                    {/* Optional value line, tucked under the header bar */}
                    {totalValue > 0 && (
                      <div className="px-3 py-1.5 text-[11px] text-slate-500 flex items-center gap-1 border-b border-slate-100 bg-slate-50/40 tabular-nums">
                        <DollarSign className="w-3 h-3 text-emerald-500" />
                        <span>{totalValue.toLocaleString()}</span>
                      </div>
                    )}

                    <Droppable droppableId={stage.value}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`flex-1 min-h-[300px] p-2 transition-colors ${
                            snapshot.isDraggingOver ? "bg-[#0F4C81]/5 ring-2 ring-inset ring-[#0F4C81]/20" : "bg-slate-50/40"
                          }`}
                        >
                          {stageProjects.map((project, index) => {
                            const contact = contacts.find(c => c.id === project.contact_id);
                            const cardValue = projectCardValue(project.id);
                            const isEstimate = projectCardValueIsEstimate(project.id);
                            return (
                              <Draggable key={project.id} draggableId={project.id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`bg-white rounded-lg border border-slate-200 p-3 mb-2 cursor-grab active:cursor-grabbing group transition-shadow ${
                                      snapshot.isDragging ? "shadow-lg ring-2 ring-[#0F4C81]/30 rotate-1" : "hover:shadow-md"
                                    }`}
                                    onClick={() => !snapshot.isDragging && navigate(`/projects/${project.id}`, { state: { from: "/pipeline" } })}
                                    data-testid={`pipeline-card-${project.id}`}
                                  >
                                    <div className="flex items-center gap-2 mb-1">
                                      <User className="w-3.5 h-3.5 text-slate-400" />
                                      <span className="font-medium text-slate-800 text-sm truncate">{contact?.name || "Unknown"}</span>
                                    </div>
                                    {project.project_address && (
                                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
                                        <MapPin className="w-3 h-3" />
                                        <span className="truncate">{project.project_address}</span>
                                      </div>
                                    )}
                                    {project.referral_name && (
                                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
                                        <span className="truncate">Referred by <span className="font-medium text-slate-700">{project.referral_name}</span></span>
                                      </div>
                                    )}
                                    {cardValue > 0 && (
                                      <div className="flex items-center justify-end">
                                        {isEstimate ? (
                                          <span
                                            className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 tabular-nums"
                                            title="Estimate — customer has not signed a quote yet"
                                            data-testid={`pipeline-card-estimate-${project.id}`}
                                          >
                                            Est <DollarSign className="w-3 h-3" />{cardValue.toLocaleString()}
                                          </span>
                                        ) : (
                                          <span className="text-xs font-medium text-emerald-600 flex items-center tabular-nums">
                                            <DollarSign className="w-3 h-3" />{cardValue.toLocaleString()}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                          {stageProjects.length === 0 && (
                            <div className="text-center py-8 text-slate-300 text-sm">Drop here</div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>

            {/* Mobile single-stage list — one stage at a time, no horizontal scroll */}
            <MobilePipelineStage
              stages={categoryStages}
              projects={projects.filter(projectMatchesSearch)}
              contacts={contacts}
              searchActive={!!searchLower}
              projectCardValue={projectCardValue}
              projectCardValueIsEstimate={projectCardValueIsEstimate}
              navigate={navigate}
              onDragEnd={onDragEnd}
            />
          </DragDropContext>
        ) : activeCategory ? (
          <div className="text-center py-16">
            <p className="text-slate-500 mb-4">No stages configured for {activeCat?.name || activeCategory}</p>
            <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => navigate("/settings")}>Configure Stages</Button>
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-slate-500 mb-4">No pipeline categories configured</p>
            <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => navigate("/settings")}>Configure Pipeline</Button>
          </div>
        )}
      </div>
    </div>
  );
}
